import React, { useState, useEffect, useCallback, useRef } from "react";
import { setState } from "../rosbridge_client";

function FamilyMemoriesScreen({
  backendUrl,
  setViewMode,
  startSlideshow,
  onBackToSetup
}) {
  const [albums, setAlbums] = useState([]);
  const [memories, setMemories] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [viewModeLocal, setViewModeLocal] = useState("carousel");

  // ---- Drive sync UI state ----
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [syncResult, setSyncResult] = useState(null);

  // Avoid double auto-sync in dev (React StrictMode)
  const didAutoSync = useRef(false);

  // -----------------------------------------------------
  // Restore viewing mode preference (persisted)
  // -----------------------------------------------------
  useEffect(() => {
    const saved = localStorage.getItem("family_memories_view_mode");
    if (saved === "carousel" || saved === "manual") {
      setViewModeLocal(saved);
      setViewMode(saved);
    }
  }, [setViewMode]);
  // -----------------------------------------------------
  // Fetch albums + photo previews
  // -----------------------------------------------------
  const loadAlbums = useCallback(async () => {
    if (!backendUrl) return;

    try {
      const res = await fetch(`${backendUrl}/api/albums`, {
        credentials: "include",
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Failed /api/albums (${res.status}): ${txt || res.statusText}`);
      }

      const albumList = await res.json();

      // Fetch preview photo for each album + filter albums with 0 photos
      const albumsWithPreviewOrNull = await Promise.all(
        albumList.map(async (album) => {
          const photosRes = await fetch(
            `${backendUrl}/api/albums/${album.id}/photos`,
            { credentials: "include" }
          );

          if (!photosRes.ok) {
            // Se fallisce la fetch, per sicurezza lo nascondiamo (evita album “vuoti”/rotti)
            return null;
          }

          const photos = await photosRes.json();

          // Se non ci sono foto, NON mostrare l'album
          if (!photos || photos.length === 0) {
            return null;
          }

          // Altrimenti lo mostriamo con preview
          return { ...album, preview: photos[0] };
        })
      );

      const albumsFiltered = albumsWithPreviewOrNull.filter(Boolean);
      setAlbums(albumsFiltered);

      if (selectedAlbum && !albumsFiltered.some((a) => a.id === selectedAlbum)) {
        setSelectedAlbum(null);
        setMemories([]);
      }
    } catch (err) {
      console.error("Error loading albums:", err);
      setSyncError(err?.message || "Error al cargar los álbumes");
    }
  }, [backendUrl, selectedAlbum]);

  useEffect(() => {
    if (!backendUrl) return;
    loadAlbums();
  }, [backendUrl, loadAlbums]);

  const loadPhotos = useCallback(async (albumId) => {
    if (!backendUrl) return;

    try {
      const res = await fetch(`${backendUrl}/api/albums/${albumId}/photos`, {
        credentials: "include",
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(
          `Failed /api/albums/${albumId}/photos (${res.status}): ${txt || res.statusText}`
        );
      }

      const data = await res.json();
      setMemories(data);
      setSelectedAlbum(albumId);
      localStorage.setItem("selected_album", String(albumId));
    } catch (err) {
      console.error("Error loading photos:", err);
      setSyncError(err?.message || "Error al cargar las fotos");
    }
  }, [backendUrl]);
    
  useEffect(() => {
    if (!backendUrl) return;

    const savedAlbumId = localStorage.getItem("selected_album");
    if (!savedAlbumId) return;

    setSelectedAlbum(savedAlbumId);
    loadPhotos(savedAlbumId);
  }, [backendUrl, loadPhotos]);

  // -----------------------------------------------------
  // Drive Sync
  // -----------------------------------------------------
  const syncWithDrive = useCallback(async () => {
    if (!backendUrl) return;
    if (syncing) return;

    setSyncing(true);
    setSyncError("");
    setSyncResult(null);

    try {
      const res = await fetch(`${backendUrl}/api/drive/sync`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        const msg =
          (data && (data.error || data.message)) ||
          `Sincronización fallida (${res.status})`;
        throw new Error(msg);
      }

      setSyncResult(data);

      // refresh albums (preview + liste aggiornate)
      await loadAlbums();

      // se un album è selezionato, ricarica anche le sue foto
      if (selectedAlbum) {
        await loadPhotos(selectedAlbum);
      }
    } catch (err) {
      console.error("Drive sync error:", err);
      setSyncError(err?.message || "Error al sincronizar con Drive");
    } finally {
      setSyncing(false);
    }
  }, [backendUrl, syncing, loadAlbums, loadPhotos, selectedAlbum]);

  // Auto-sync once when opening this screen
  useEffect(() => {
    if (!backendUrl) return;
    if (didAutoSync.current) return;
    didAutoSync.current = true;

    syncWithDrive();
  }, [backendUrl, syncWithDrive]);

  // -----------------------------------------------------
  // UI
  // -----------------------------------------------------
  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Álbum de Fotos</h1>
        <p style={styles.subtitle}>
          Selecciona un álbum y el modo de visualización para comenzar la presentación.
        </p>

        {/* Drive sync bar */}
        {/*<div style={styles.syncBar}>
          <button
            style={{
              ...styles.syncButton,
              opacity: syncing ? 0.7 : 1,
              cursor: syncing ? "not-allowed" : "pointer",
            }}
            onClick={syncWithDrive}
            disabled={syncing}
          >
            {syncing ? "Syncing with Drive..." : "Sync with Drive"}
          </button>*/}

          {/* Result / error */}
          {/*<div style={styles.syncInfo}>
            {syncError ? (
              <div style={styles.syncError}>{syncError}</div>
            ) : syncResult ? (
              <div style={styles.syncResult}>
                <span>Downloaded: {syncResult.downloaded ?? 0}</span>
                <span style={styles.dot}>•</span>
                <span>Updated: {syncResult.updated ?? 0}</span>
                <span style={styles.dot}>•</span>
                <span>Removed: {syncResult.removed ?? 0}</span>
              </div>
            ) : null}
          </div>
        </div>*/}
      </div>

      {/* Viewing Mode Section */}
      {/*<div style={styles.section}>
        <h2 style={styles.sectionTitle}>Modo de visualización</h2>

        <div style={styles.radioGroup}>
          <label style={styles.radioItem}>
            <input
              type="radio"
              checked={viewModeLocal === "carousel"}
              onChange={() => {
                const mode = "carousel";
                setViewModeLocal(mode);
                setViewMode(mode);
                localStorage.setItem("family_memories_view_mode", mode);
              }}
            />
            <span style={{ marginLeft: 8 }}>Carrusel automático</span>
          </label>

          <label style={styles.radioItem}>
            <input
              type="radio"
              checked={viewModeLocal === "manual"}
              onChange={() => {
                const mode = "manual";
                setViewModeLocal(mode);
                setViewMode(mode);
                localStorage.setItem("family_memories_view_mode", mode);
              }}
            />
            <span style={{ marginLeft: 8 }}>Navegación manual</span>
          </label>
        </div>
      </div>*/}

      {/* Album Section */}
      <div style={styles.section}>
        {/* <h2 style={styles.sectionTitle}>Álbumes</h2> */}

        <div style={styles.albumList}>
          {albums.length === 0 && <p>No se han encontrado álbumes.</p>}

          {albums.map((a) => (
            <div
              key={a.id}
              style={{
                ...styles.albumItem,
                borderColor: selectedAlbum === a.id ? "#225bff" : "#ccc",
              }}
              onClick={() => loadPhotos(a.id)}
            >
              {a.preview ? (
                <img
                  src={`${backendUrl}${a.preview.url.startsWith("/") ? "" : "/"}${a.preview.url}`}
                  alt="vista previa"
                  style={styles.albumPreview}
                />
              ) : (
                <div style={styles.missingPreview}>Sin foto</div>
              )}
              <p style={styles.albumLabel}>{a.title}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Start slideshow */}
      {/*<button
        style={{
          ...styles.primaryButton,
          opacity: memories.length === 0 ? 0.6 : 1,
          cursor: memories.length === 0 ? "not-allowed" : "pointer",
        }}
        onClick={() => startSlideshow(memories)}
        disabled={memories.length === 0}
      >
        Iniciar presentación
      </button>*/}

      {/* Bottom actions: view mode + start */}
      <div style={styles.bottomBar}>
        <div style={styles.modeBlock}>
          <div style={styles.modeLabel}>Modo de visualización</div>

          <div style={styles.viewModeSwitch}>
            <button
              type="button"
              style={{
                ...styles.switchButton,
                ...(viewModeLocal === "carousel" ? styles.switchButtonActive : {}),
              }}
              onClick={() => {
                const mode = "carousel";
                setViewModeLocal(mode);
                setViewMode(mode);
                localStorage.setItem("family_memories_view_mode", mode);
              }}
            >
              Carrusel automático
            </button>

            <button
              type="button"
              style={{
                ...styles.switchButton,
                ...(viewModeLocal === "manual" ? styles.switchButtonActive : {}),
              }}
              onClick={() => {
                const mode = "manual";
                setViewModeLocal(mode);
                setViewMode(mode);
                localStorage.setItem("family_memories_view_mode", mode);
              }}
            >
              Manual
            </button>
          </div>
        </div>


        <button
          style={{
            ...styles.primaryButton,
            opacity: memories.length === 0 ? 0.6 : 1,
            cursor: memories.length === 0 ? "not-allowed" : "pointer",
          }}
          onClick={() => startSlideshow(memories)}
          disabled={memories.length === 0}
        >
          Iniciar presentación
        </button>
      </div>


      {/* Back */}
      {/* <button
        style={styles.secondaryButton}
        onClick={() => {
          setState("IDLE");
          onBackToSetup();
        }}
      >
        🔙 Back to Setup
      </button> */}
    </div>
  );
}

//
// 🎨 STYLES — responsive per lo schermo Temi
//
const styles = {
  page: {
    backgroundColor: "#ffffff",
    minHeight: "100vh",
    padding: "10px 6px 24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontFamily: "Arial, sans-serif",
  },

  header: {
    textAlign: "center",
    marginBottom: "10px",
    width: "100%",
    maxWidth: "650px",
  },

  title: {
    fontSize: "48px",
    fontWeight: "bold",
    marginBottom: "4px",
  },

  subtitle: {
    fontSize: "30px",
    color: "#555",
    marginTop: "0px",
    maxWidth: "800px",
    lineHeight: "1.2",
    marginLeft: "auto",
    marginRight: "auto",
  },

  // ---- Sync UI ----
  syncBar: {
    marginTop: "10px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
  },

  syncButton: {
    padding: "10px 22px",
    backgroundColor: "#0b6b2a",
    color: "white",
    borderRadius: "22px",
    border: "none",
    fontSize: "16px",
  },

  syncInfo: {
    minHeight: "18px",
  },

  syncResult: {
    fontSize: "14px",
    color: "#222",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    flexWrap: "wrap",
  },

  dot: {
    margin: "0 4px",
    color: "#888",
  },

  syncError: {
    fontSize: "14px",
    color: "#b00020",
    maxWidth: "600px",
    wordBreak: "break-word",
  },

  section: {
    width: "100%",
    maxWidth: "1200px",
    marginTop: "0px",
    marginBottom: "0px",
    textAlign: "center",
  },

  sectionTitle: {
    fontSize: "28px",
    marginBottom: "10px",
  },

  radioGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },

  radioItem: {
    fontSize: "16px",
  },

  albumList: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: "28px",
    marginTop: "4px",
    width: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    paddingBottom: "6px",
    paddingLeft: "0px",
    paddingRight: "0px",
    scrollbarWidth: "none",
    WebkitOverflowScrolling: "touch" // <-- NEW: scroll fluido su touch
  },

  albumItem: {
    width: "350px",
    height: "350px",
    flex: "0 0 auto",
    borderRadius: "20px",
    border: "3px solid #ddd",
    cursor: "pointer",
    padding: "16px",
    backgroundColor: "#fafafa",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    transition: "0.25s",
  },

  albumPreview: {
    width: "100%",
    height: "250px",
    objectFit: "cover",
    borderRadius: "18px",
  },

  missingPreview: {
    width: "100%",
    height: "200px",
    backgroundColor: "#ddd",
    borderRadius: "16px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "18px",
    color: "#666",
  },

  albumLabel: {
    fontSize: "30px",
    fontWeight: "800",
    marginTop: "8px",
    marginBottom: "8px",
    color: "#222",
  },

  primaryButton: {
    width: "320px",
    height: "140px",
    padding: "18px 24px",
    backgroundColor: "#225bff",
    color: "white",
    borderRadius: "18px",
    border: "none",
    fontSize: "32px",
    fontWeight: 900,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    boxShadow: "0 8px 20px rgba(0,0,0,0.14)",
  },

  secondaryButton: {
    padding: "10px 22px",
    backgroundColor: "#444",
    color: "white",
    borderRadius: "26px",
    border: "none",
    fontSize: "16px",
    cursor: "pointer",
    marginTop: "18px",
  },

  // bottomBar: {
  //   width: "100%",
  //   maxWidth: "900px",
  //   display: "flex",
  //   alignItems: "flex-end",
  //   justifyContent: "space-between",
  //   gap: "44px",            // più spazio tra blocco sinistro e bottone start
  //   marginTop: "auto",      // spinge la barra in basso
  //   paddingTop: "18px",
  // },

  bottomBar: {
    position: "fixed",
    left: "50%",
    transform: "translateX(-50%)",
    bottom: "24px",          // <-- distanza precisa dal bordo inferiore
    width: "100%",
    maxWidth: "900px",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "44px",
    paddingTop: "0px",
  },

  modeBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "12px",
  },

  modeLabel: {
    fontSize: "30px",
    fontWeight: 800,
    color: "#111",
  },

  viewModeSwitch: {
    display: "flex",
    gap: "18px",
    alignItems: "stretch",  // IMPORTANT: stessa altezza tra i due bottoni
  },

  switchButton: {
    width: "260px",
    height: "110px",        // IMPORTANT: altezza fissa uguale anche con testo più lungo
    padding: "14px 18px",
    backgroundColor: "#ffffff",
    color: "#111",
    borderRadius: "18px",
    border: "4px solid #888",
    fontSize: "30px",
    fontWeight: 800,
    lineHeight: 1.05,
    cursor: "pointer",
    display: "flex",        // centra testo e gestisce multilinea
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    boxShadow: "0 6px 16px rgba(0,0,0,0.10)",
  },

  switchButtonActive: {
    borderColor: "#225bff",
    boxShadow: "0 8px 20px rgba(34,91,255,0.20)",
  },
};

export default FamilyMemoriesScreen;
